import { Field, useFormikContext } from 'formik'
import React from 'react'

import { InnerField } from '@components/common/InnerField'
import { ButtonClean, Checkbox, Fieldset, Icon, Tooltip } from '@fourthwall/components'
import { QuestionMarkIcon } from '@fourthwall/icons'

import type { FormValues } from '../../Shipping.types'
import { ShippingGroupTile } from '../ShippingGroupTile'
import * as S from './ShippingGroupsYouShip.styled'
import type { ShippingGroup } from './ShippingGroupsYouShip.types'
import { ShippingGroupEmptyState } from './components/ShippingGroupEmptyState/ShippingGroupEmptyState'
import { getShippingGroupLink } from './utils/getShippingGroupLink'

type ShippingGroupsYouShipViewProps = {
  shippingGroups: ShippingGroup[]
  disabled: boolean
  onEdit: (id: string) => void
  onDelete: (id: string, name: string) => void
}

export const ShippingGroupsYouShipView = ({
  shippingGroups,
  disabled,
  onEdit,
  onDelete,
}: ShippingGroupsYouShipViewProps) => {
  const { values } = useFormikContext<FormValues>()

  const renderShippingGroups = () => {
    if (!shippingGroups.length) {
      return <ShippingGroupEmptyState to={getShippingGroupLink()} />
    }

    return (
      <>
        <S.ScrollContainer maxHeight={600}>
          <S.ShippingGroups>
            {shippingGroups.map((shippingGroup, index) => {
              const name = `Shipping group ${index + 1}`

              return (
                <ShippingGroupTile
                  key={shippingGroup.id || name}
                  name={name}
                  offerIds={shippingGroup.offerIds}
                  countries={shippingGroup.countries}
                  onEdit={() => onEdit(shippingGroup.id)}
                  onDelete={() => onDelete(shippingGroup.id, name)}
                />
              )
            })}
          </S.ShippingGroups>
        </S.ScrollContainer>
        <ButtonClean label="Create a shipping group" to={getShippingGroupLink()} />
      </>
    )
  }

  return (
    <>
      <Fieldset.Row>
        <div>
          <Field
            component={Checkbox}
            disabled={disabled}
            name="withShippingGroupsYouShip"
            label={
              <>
                Restrict the countries where I ship my products
                {disabled && (
                  <S.TooltipContainer>
                    <Tooltip
                      reference={<Icon component={QuestionMarkIcon} height={16} />}
                      placement="top"
                      strategy="fixed"
                    >
                      You have not yet created any products that you are shipping.
                    </Tooltip>
                  </S.TooltipContainer>
                )}
              </>
            }
          />
        </div>
      </Fieldset.Row>
      {values.withShippingGroupsYouShip && (
        <InnerField fluid>
          <S.ShippingGroups>{renderShippingGroups()}</S.ShippingGroups>
        </InnerField>
      )}
    </>
  )
}
